<template>
  <v-container class="pl-0">
    <v-card-title class="pa-1">
      <h3 :class="fieldError ? 'red--text wiggle' : 'white--text'">{{ field.label }}</h3>
    </v-card-title>
    <v-card-subtitle v-if="field.helpText" class="pa-1">
      <p class="mt-1 grey--text text--lighten-1 text-caption font-weight-regular">{{ field.helpText }}</p>
    </v-card-subtitle>
    <v-col v-if="!field.multivalued && newValue" :cols="fullForm ? 3 : 5" class="pl-0">
      <CloudinarySEOItem
        v-if="newValue.url"
        v-model="newValue"
        :raw-content-types="rawContentTypes"
        :cloudinary-loading="cloudinaryLoading"
        :field="field"
        @get-cloudinary-resource="getCloudinarySingleFieldResource()"
        @launch-cloudinary="setImageValue()"
        @delete-resource="deleteFieldResource()"
      />
    </v-col>
    <draggable
      v-else
      v-model="newValue"
      :disabled="!store.getters.permissionToWrite || loading.length > 0 || cloudinaryLoading || (newValue && newValue.length <= 1)"
      class="d-flex flex-wrap"
      handle=".drag-handle"
      style="width: 100%"
    >
      <v-col v-for="(resource, resourceIndex) in newValue" :key="resourceIndex" :cols="fullForm ? 3 : 5" class="item">
        <CloudinarySEOItem
          v-if="resource?.url"
          v-model="newValue[resourceIndex]"
          :raw-content-types="rawContentTypes"
          :items-loading="loading.length > 0"
          :cloudinary-loading="cloudinaryLoading"
          :field="field"
          :show-drag-handle="newValue.length > 1 && loading.length <= 0 && !cloudinaryLoading"
          @launch-cloudinary="editImageValueInArray(resourceIndex)"
          @delete-resource="deleteFieldResource(resourceIndex)"
        />
      </v-col>
    </draggable>
    <div v-if="store.getters.permissionToWrite && (field.multivalued || (!field.multivalued && !newValue?.url))">
      <v-btn v-if="!cloudinaryLoading" icon @click="props.field?.multivalued ? newMultipleImages() : setImageValue()">
        <v-icon :color="fieldError ? 'red' : ''">add_photo_alternate</v-icon>
      </v-btn>
      <v-progress-circular v-else class="loading" :width="3" :size="25" indeterminate color="info" />
    </div>

    <v-card-subtitle v-if="fieldError" class="px-0 py-0">
      <span class="red--text">{{ fieldError }}</span>
    </v-card-subtitle>

    <v-text-field v-show="false" ref="cloudinaryRules" v-model="newValue" :rules="field.rules" readonly />
  </v-container>
</template>

<script setup>
import { useStore } from 'vue2-helpers/vuex';

import { ref, computed, watch } from 'vue';

import draggable from 'vuedraggable';

import CloudinarySEOItem from './CloudinarySEOItem.vue';

// eslint-disable-next-line no-undef
const emit = defineEmits(['input']);

// eslint-disable-next-line no-undef
const props = defineProps({
  field: { type: Object, default: () => ({}) },
  value: { type: [Array, Object, String], default: null },
  fullForm: { type: Boolean, default: false }, // used for vuetify col size
  rawContentTypes: { type: Object, default: () => ({}) }
});

const store = useStore();

const newValue = ref(!props.value && props.field.multivalued ? [] : JSON.parse(JSON.stringify(props.value)));

const loading = ref(props.field.multivalued ? [] : false);
const cloudinaryLoading = ref(false);
const changed = ref(false);

const cloudinaryRules = ref(null);
const fieldError = computed(() => {
  if (!changed.value) return null;
  if (cloudinaryRules.value && cloudinaryRules.value.valid) return null;
  return (cloudinaryRules.value && cloudinaryRules.value.errorBucket.length) ? cloudinaryRules.value.errorBucket[0] : null;
});

const launchCloudinary = (multiple, callback) => {
  cloudinaryLoading.value = true;

  const config = { ...store.state.cloudinaryConfig, multiple: multiple };

  // eslint-disable-next-line no-undef
  cloudinary.openMediaLibrary(
    config,
    {
      insertHandler: data => {
        const imgs = data.assets.map(asset => {
          // Gets the path and name of the resource (ex: /folderA/folderB/name.jpeg)
          const { public_id } = asset;

          // Gets the derived transformation in case it has one
          let derivation = '';
          if (asset.derived) {
            derivation = `/${asset.derived[0].raw_transformation}`;
          }

          // Gets extension of the file
          const extension = asset.format;

          // Generates the url
          return { url: `${derivation}/${public_id}.${extension}` };
        });
        callback(imgs);
      },
      hideHandler: () => {
        cloudinaryLoading.value = false;
      }
    }
  );
};

const setImageValue = () => {
  launchCloudinary(false, imgs => {
    newValue.value = { ...newValue.value, ...imgs[0] };
  });
};

const newMultipleImages = () => {
  launchCloudinary(true, imgs => {
    if (!newValue.value) {
      newValue.value = [];
    }
    newValue.value.push(...imgs);
  });
};

const editImageValueInArray = idx => {
  launchCloudinary(false, imgs => {
    newValue.value[idx] = { ...newValue.value[idx], ...imgs[0] };
    newValue.value = [...newValue.value];
  });
};

const deleteFieldResource = index => {
  if (typeof index === 'number') {
    newValue.value.splice(index, 1);
  } else {
    newValue.value = null;
  }
};

// Watching complex ref data types: https://www.netlify.com/blog/2021/01/29/deep-dive-into-the-vue-composition-apis-watch-method/#complex
const watchValue = (props.field.multivalued ? () => [...newValue.value] : newValue);
watch(watchValue, value => {
  changed.value = true;

  emit('input', value);
});

</script>
